/**
 * Dynatrace User Context Integration
 *
 * This module provides functions to set user and tenant context in Dynatrace RUM
 * for correlating events with specific users and tenants.
 */

import {
	identifyDynatraceUser,
	sendDynatraceSessionProperties,
	isDynatraceLoaded,
} from './dynatrace';

/**
 * Set user context in Dynatrace
 * This should be called after successful authentication
 *
 * @param userId - The internal user identifier (not email/name for privacy)
 * @param tenantId - The tenant/organization identifier
 * @param stringProperties - Optional additional string properties (appVersion, etc.)
 */
export const setDynatraceUserContext = (
	userId: string,
	tenantId: string,
	stringProperties?: Record<string, string | number>,
): void => {
	if (!isDynatraceLoaded()) {
		console.warn('[Dynatrace] RUM not loaded, skipping user context setup');
		return;
	}

	try {
		// Identify user with composite ID that includes tenantId
		// This populates internalKeyUserActionId in the userAction table
		const compositeUserId = `${userId}_tenant_${tenantId}`;
		identifyDynatraceUser(compositeUserId);

		// Build string properties object, converting all values to strings
		const stringPropertiesObject: Record<string, string> = {
			tenantId: String(tenantId),
		};

		// Add additional string properties
		if (stringProperties) {
			Object.entries(stringProperties).forEach(([key, value]) => {
				stringPropertiesObject[key] = String(value);
			});
		}

		sendDynatraceSessionProperties(stringPropertiesObject);

		console.info('[Dynatrace] User context set successfully', {
			userId,
			tenantId,
			stringProperties: Object.keys(stringProperties || {}),
		});
	} catch (error) {
		console.error('[Dynatrace] Failed to set user context', error);
	}
};

/**
 * Clear user context (on logout)
 */
export const clearDynatraceUserContext = (): void => {
	if (!isDynatraceLoaded() || !window.dtrum) {
		return;
	}

	try {
		// End the current session
		window.dtrum.endSession?.();
		console.info('[Dynatrace] User context cleared');
	} catch (error) {
		console.error('[Dynatrace] Failed to clear user context', error);
	}
};
